import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import { COUPONS_ID, PROMOCODE_CODE } from 'constants/apis';
import axios from 'helpers/axios';
import { useTranslation } from 'react-i18next';
import { ToastContext } from 'contexts';

const isCoupon = discount => !!(discount && discount.object === 'coupon');
const isPromotionCode = discount => !!(discount && discount.object === 'promotion_code');

const validateDiscountCode = async discountCode => {
  const endpoint =
    process.env.REACT_APP_USE_PROMO_CODES === '1'
      ? PROMOCODE_CODE(discountCode)
      : COUPONS_ID(discountCode);

  const { data } = await axios.get(endpoint);
  return data;
};

/**
 * Checks the validity of a discount code.
 *
 * A discount code can either be a coupon or promotion code.
 *
 * @returns {object} An object containing the function to trigger the async operation and its result.
 */
const useDiscountCode = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);

  const { data, mutate, ...result } = useMutation(validateDiscountCode, {
    onError: () => {
      // We use the 'coupon' terminology for user facing things despite whether
      // or not they are using a coupon OR promotion code.
      triggerToast({ message: t('subscribe.errors.invalid_coupon') });
    },
  });

  return {
    discount: data || {},
    validateDiscountCode: mutate,
    isCoupon: isCoupon(data),
    isPromotionCode: isPromotionCode(data),
    ...result,
  };
};

export default useDiscountCode;
